/**
 * The desktop-only "Import / export" flow, opened from the `NavRail`'s
 * account menu (behind `import_export_v1`). A focused
 * dialog beats the old approach of scrolling to a card buried at the bottom
 * of a long `/profile` page: pick a direction, then walk through it with the
 * motion catalog's `wizardStep()` transition (lib/motion.ts §wizardStep).
 *
 * Reuses `ExportSection`/`ImportWizard` from `DataExportImport.tsx` for the
 * actual export/import logic (polling, resume, cancel) rather than
 * duplicating it — this component is presentation only.
 */
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, Download, Upload } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLocaleOptional } from '@/i18n/locale';
import { pressable, wizardStep } from '@/lib/motion';

import { ExportSection, ImportWizard } from './DataExportImport';

type Step = 'choose' | 'export' | 'import';

export function ImportExportWizard({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLocaleOptional();
  const [step, setStep] = useState<Step>('choose');
  const [direction, setDirection] = useState<1 | -1>(1);

  const goTo = (next: Step, dir: 1 | -1) => {
    setDirection(dir);
    setStep(next);
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      // Reset after the close animation so the dialog doesn't visibly jump
      // back to "choose" while it's still fading out.
      window.setTimeout(() => goTo('choose', -1), 200);
    }
  };

  const title =
    step === 'choose'
      ? t('profile.yourData')
      : step === 'export'
        ? t('profile.yourData.export')
        : t('profile.yourData.import');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-hidden">
        <DialogHeader className="flex-row items-center gap-2 space-y-0">
          {step !== 'choose' && (
            <button
              type="button"
              onClick={() => goTo('choose', -1)}
              aria-label={t('common.back')}
              className="-ml-1.5 rounded p-1.5 text-ink-tertiary transition-colors hover:bg-surface-alt hover:text-ink"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            </button>
          )}
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            variants={wizardStep(direction)}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {step === 'choose' && (
              <div className="grid grid-cols-2 gap-3">
                <ChoiceCard
                  icon={Upload}
                  label={t('profile.yourData.import')}
                  hint={t('profile.yourData.importHint')}
                  onClick={() => goTo('import', 1)}
                />
                <ChoiceCard
                  icon={Download}
                  label={t('profile.yourData.export')}
                  hint={t('profile.yourDataHint')}
                  onClick={() => goTo('export', 1)}
                />
              </div>
            )}
            {step === 'export' && <ExportSection />}
            {step === 'import' && (
              <ImportWizard onGoToLibrary={() => handleOpenChange(false)} />
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

function ChoiceCard({
  icon: Icon,
  label,
  hint,
  onClick,
}: {
  icon: typeof Upload;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex flex-col items-start gap-3 rounded-lg bg-surface p-5 text-left hover:bg-surface-alt"
      {...pressable()}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent-ink">
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </span>
      <span aria-hidden className="text-headline font-semibold text-ink">
        {label}
      </span>
      <span aria-hidden className="text-caption text-ink-secondary">
        {hint}
      </span>
    </motion.button>
  );
}
